import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {Preferences} from "@khan/guide-ui";
import {standardTransition} from "@khan/shared-styles";

import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";

import xIcon from "@phosphor-icons/core/regular/x.svg";

// As designed.
const PREFERENCES_WIDTH = 400;

const fadeTransition = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    enter: {
        opacity: 0,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    enterActive: {
        opacity: 1,
        transition: standardTransition,
        transitionProperty: "opacity",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    leave: {
        opacity: 1,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    leaveActive: {
        opacity: 0,
        transition: standardTransition,
        transitionProperty: "opacity",
    },
});

export type Props = {
    /* Whether the drawer is open or not. */
    open: boolean;
    /* Callback to close the drawer. */
    onClose: () => unknown;
    /* The children to render in the drawer. */
    children: React.ReactNode;
};

/**
 * A drawer to show/hide the preferences form.
 *
 * @param open
 * @param onClose
 * @param children
 * @constructor
 */
const PrefsDrawer = ({open, onClose, children}: Props): React.ReactElement => {
    const ref = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (open) {
            ref.current?.focus?.();
        }
    }, [open]);

    return (
        <>
            {children}
            <AphroditeCSSTransitionGroup
                transitionLeaveTimeout={200}
                transitionStyle={fadeTransition}
            >
                {open ? (
                    <View style={styles.backdrop}>
                        <View style={styles.formContainer}>
                            <IconButton
                                ref={ref}
                                style={styles.closeIcon}
                                onClick={onClose}
                                icon={xIcon}
                                kind="tertiary"
                            />
                            <Preferences fullPageStyles={true} />
                        </View>
                    </View>
                ) : null}
            </AphroditeCSSTransitionGroup>
        </>
    );
};

export default PrefsDrawer;

const styles = StyleSheet.create({
    backdrop: {
        backgroundColor: color.offBlack64,
        position: "absolute",
        width: "100%",
        height: "100%",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "flex-end",
    },
    formContainer: {
        backgroundColor: "white",
        width: PREFERENCES_WIDTH,
        height: "100%",
        borderRadius: 4,
    },
    closeIcon: {
        position: "relative",
        color: color.offBlack,
        // Arbitrary positioning for close icon.
        top: 16,
        left: 350,
        zIndex: 2,
    },
});
